import React, { useEffect, useState } from 'react';
import { fmtDateTime, fmtNumber } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { api, DropFolderFile } from '../../utils/api';
import Pagination from '../Pagination';
import { darken } from '../../tabPalette';

// ── Pick a file from the Drop Folder (Calem, report 296f15b9) ───────────────────────────────────────
// "When a user is requested to upload a document for the completion of a job, direct them to upload
// from the drop folder not their local C drive/PC" — and, asked: "Only from the Drop Folder." The
// Complete popup's documents (JobStatusConfirm) open this in place of the computer's own file window:
// the files waiting in the shared Drop Folder, newest first, each with who put it there and when.
// Picking one hands it back to the dialog, which files it under the name it shows; the server moves the
// Drop Folder copy to the Trash (server/src/services/dropFile.ts) only once that popup's Confirm & Send
// goes through (report 1ea970ae; `untilSend` makes the hint say so). Closed Jobs' manual review picked
// here too until report 6cbd0c16; it matches a document already in Our Documents now (OurDocsPicker).
// data-drop-picker: typing into its search box is work in the Complete popup, not typing elsewhere, so
// JobStatusConfirm's typing pause leaves it alone.
// `examples` (the Re-entry Stamp's row only, report 603af598): the naming examples are listed too, first, each
// labelled "example" as the File Storage page labels it — with no stamp, the "NO Re-entry Stamp" example is the
// answer, and it stays in the Drop Folder after use. A date in a file's name shows as 19 SEP 2027 in a pink
// bubble (DropFileName, below).
// The email composers' paperclip opens it too (report 2454c57a, components/AttachFileMenu), with its own `hint` in
// place of the Trash one: a file attached to an email is only copied into it and stays in the Drop Folder.
// There it also hands over what the email says about whom it is for (`suggest`, 2nd opening: "give them hints in a
// list based on the email information"): the job number in its subject and the words of its client's name. Each is
// looked for across the whole Drop Folder, with the search the box makes, one at a time; the files whose NAMES hold
// them are listed first under "Suggested for this email", best fit first: the job number counts most, then how many
// of the name's words the file name holds, newest first among equals, ten at most. The full list follows as before,
// so nothing is hidden. Shown on the first page while the search box is empty; typing searches the full list alone.

// When it was put there, in the region's date format and the tenant's zone: "28/09/2026 14:30".
const fmtAt = (sec: number) => (sec ? fmtDateTime(sec) : '—');
// As the File Library's Size column writes it.
const fmtSize = (n: number) => (!n ? '—' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const iconOf = (mime: string) => (mime === 'application/pdf' ? 'ti-file-type-pdf' : /^image\//.test(mime) ? 'ti-photo' : 'ti-file');

// ── The date in a Drop Folder file's name (report 02661349, 3rd and 4th openings) ───────────────────────
// "In the drop folder ONLY, change the date format from YYYY-MM-DD to DD SEP YYYY (example, 19 SEP 2027) and
// highlight the drop folder date with a pink bubble only around the date." Shown so, never stored so: the name
// keeps the YYYY-MM-DD the naming protocol writes (server/src/services/jobCompletionDocs.ts, nameDate), so a
// search, a rename and the copy a job files read the name as it always was. Drawn in this picker and in the Drop
// Folder's own list (FileLibrary, its in_drop rows); every other folder shows its names as they are. The bubble
// was the pink the File Library marks the file just saved with (its justSaved row, which keeps it) until the 4th
// opening: "Make the pink highlight darker by 3 shades and make the date text white." So it is that pink three
// shades darker, DATE_PINK, each shade a quarter of the way on to the solid #EC4899 (see darken in tabPalette):
// 0.14 → 0.36 → 0.52 → 0.64, so on white #FCE5F1 → #F38ABE. Taken one shade at a time, as each "one shade darker"
// has gone, never darken(x, 3). The date in it is written white; the rest of the name keeps its own ink.
const FOCUS_PINK = 'rgba(236,72,153,0.14)';
const DATE_PINK = darken(darken(darken(FOCUS_PINK, 1), 1), 1);
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const pad2 = (n: number) => String(n).padStart(2, '0');
// A real calendar day: "2027-02-31" has the shape but is none, and is left as it is written.
const realDay = (y: number, m: number, d: number) => {
  const x = new Date(Date.UTC(y, m - 1, d));
  return x.getUTCFullYear() === y && x.getUTCMonth() === m - 1 && x.getUTCDate() === d;
};
// Every YYYY-MM-DD in a name that is a real day: where it starts and ends in the name, and the day.
function isoDatesIn(name: string): Array<{ from: number; to: number; y: number; m: number; d: number }> {
  const out: Array<{ from: number; to: number; y: number; m: number; d: number }> = [];
  const rx = /(^|\D)((?:19|20)\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?!\d)/g;
  let hit: RegExpExecArray | null;
  while ((hit = rx.exec(name))) {
    const y = Number(hit[2]), m = Number(hit[3]), d = Number(hit[4]);
    if (realDay(y, m, d)) out.push({ from: hit.index + hit[1].length, to: hit.index + hit[0].length, y, m, d });
  }
  return out;
}
/** The date a file's name carries, as YYYY-MM-DD — written YYYY-MM-DD or DD MMM YYYY ("19 SEP 2027",
 *  "19-Sep-2027") — or '' when it carries none. The last one when there are several: the naming protocol
 *  puts the date at the end. The Complete popup fills a picked file's date box with it. */
export function dateInName(name: string): string {
  const s = String(name || '');
  let best = { at: -1, iso: '' };
  for (const x of isoDatesIn(s)) if (x.from > best.at) best = { at: x.from, iso: `${x.y}-${pad2(x.m)}-${pad2(x.d)}` };
  const rx = /(^|[^\dA-Za-z])(0?[1-9]|[12]\d|3[01])[ -]([A-Za-z]{3})[ -]((?:19|20)\d{2})(?!\d)/g;
  let hit: RegExpExecArray | null;
  while ((hit = rx.exec(s))) {
    const y = Number(hit[4]), m = MON.indexOf(hit[3].toUpperCase()) + 1, d = Number(hit[2]);
    const at = hit.index + hit[1].length;
    if (m && realDay(y, m, d) && at > best.at) best = { at, iso: `${y}-${pad2(m)}-${pad2(d)}` };
  }
  return best.iso;
}
/** A Drop Folder file's name with each YYYY-MM-DD in it written DD MMM YYYY inside a pink bubble. */
export function DropFileName({ name }: { name: string }) {
  const dates = isoDatesIn(name);
  if (!dates.length) return <>{name}</>;
  const parts: React.ReactNode[] = [];
  let at = 0;
  dates.forEach((x, i) => {
    if (x.from > at) parts.push(name.slice(at, x.from));
    parts.push(<span key={i} style={{ padding: '1px 6px', borderRadius: 999, background: DATE_PINK, color: '#fff' }}>{`${pad2(x.d)} ${MON[x.m - 1]} ${x.y}`}</span>);
    at = x.to;
  });
  if (at < name.length) parts.push(name.slice(at));
  return <>{parts}</>;
}

/** What an email says about whom it is for (components/AttachFileMenu): its subject's job number, 0 for none, and the
 *  words of its client's name. */
export type DropSuggest = { jobNo: number; words: string[] };
const SUGGEST_MAX = 10;
// A name's words as a search compares them: letters, marks and figures, lower case (Thai keeps its vowel and tone marks).
const wordsIn = (s: string) => s.toLowerCase().split(/[^\p{L}\p{M}\p{N}]+/u).filter(Boolean);
/** How well a file's name fits the email: does it hold the job number, and how many of the client's name words. The
 *  number may be written with its thousands mark ("12,345") but never sits inside a longer number; a word of three
 *  letters or more may sit inside a longer one ("SmithJohn_will.pdf"), a shorter one must stand alone. */
function fitOf(name: string, s: DropSuggest): { job: boolean; words: number } {
  const n = name.toLowerCase();
  const job = !!s.jobNo && new RegExp(`(^|\\D)${s.jobNo}(?!\\d)`).test(n.replace(/(\d)[,.](?=\d{3}(?!\d))/g, '$1'));
  const own = wordsIn(n);
  const words = s.words.filter(w => { const x = w.toLowerCase(); return x.length >= 3 ? n.includes(x) : own.includes(x); }).length;
  return { job, words };
}

export default function DropFolderPicker({ docName, onPick, onClose, examples = false, untilSend = false, hint = '', suggest }: {
  docName: string; onPick: (f: DropFolderFile) => void; onClose: () => void; examples?: boolean; untilSend?: boolean; hint?: string;
  suggest?: DropSuggest;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [beat, setBeat] = useState(0);   // Refresh
  const [data, setData] = useState<{ rows: DropFolderFile[]; total: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Suggested for this email (see the top): null until looked up, and with nothing to look up.
  const [sugg, setSugg] = useState<DropFolderFile[] | null>(null);
  const sJob = suggest?.jobNo || 0;
  const sWords = (suggest?.words || []).join(' ');
  useEffect(() => {
    const s: DropSuggest = { jobNo: sJob, words: sWords ? sWords.split(' ') : [] };
    if (!s.jobNo && !s.words.length) { setSugg(null); return; }
    let live = true;
    // The job number as it is stored and as the region groups it ("12345", "12,345"), then each word of the name.
    const terms = Array.from(new Set([...(s.jobNo ? [String(s.jobNo), fmtNumber(s.jobNo)] : []), ...s.words]));
    Promise.all(terms.map(term => api.dropFolderFiles(term, 1).then(d => d.rows || []).catch(() => [] as DropFolderFile[])))
      .then(lists => {
        if (!live) return;
        const byId = new Map<string, DropFolderFile>();
        lists.forEach(l => l.forEach(f => { if (f && !byId.has(f.id)) byId.set(f.id, f); }));
        // The search also matches who put a file there; only a file's own name counts here.
        const fits = Array.from(byId.values()).map(f => ({ f, fit: fitOf(f.name, s) })).filter(x => x.fit.job || x.fit.words > 0);
        fits.sort((a, b) => Number(b.fit.job) - Number(a.fit.job) || b.fit.words - a.fit.words || b.f.at - a.f.at);
        setSugg(fits.slice(0, SUGGEST_MAX).map(x => x.f));
      });
    return () => { live = false; };
  }, [sJob, sWords, beat]);
  useEffect(() => {
    let live = true;
    // A search waits for a pause in typing; opening, paging and Refresh ask at once.
    const id = setTimeout(() => {
      setLoading(true);
      api.dropFolderFiles(q.trim(), page, examples)
        .then(d => { if (live) { setData({ rows: d.rows || [], total: Number(d.total) || 0, limit: Number(d.limit) || 100 }); setErr(''); setLoading(false); } })
        .catch((e: any) => { if (live) { setErr(e?.message || 'Failed to load the Drop Folder'); setLoading(false); } });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(id); };
  }, [q, page, beat, examples]);

  const rows = data?.rows || [];
  // Suggested for this email: above the first page of the full list, while nothing is typed in the box.
  const showSugg = !!(sJob || sWords) && !q.trim() && page === 1 && !err && rows.length > 0;
  const head: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '9px 20px 6px', background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)' };
  const quiet: React.CSSProperties = { fontSize: 12, color: 'var(--text-tertiary)', padding: '9px 20px', lineHeight: 1.45, borderBottom: '1px solid var(--border)' };
  const fileRow = (f: DropFolderFile, key: string) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
      <button type="button" onClick={() => onPick(f)}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px 8px 20px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text-primary)' }}>
        <i className={`ti ${iconOf(f.mime)}`} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><DropFileName name={f.name} /></span>
            {f.example && <span title={t('fl_example_hint')} style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>{t('fl_example')}</span>}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t('jdp_by', { name: f.byName || '—' })} · {fmtAt(f.at)} · {fmtSize(f.size)}</span>
        </span>
      </button>
      <button type="button" title={t('jdp_view')} onClick={() => { api.openLibraryFile(f.id, f.name).catch(() => { /* its tab closes again; the list stays */ }); }}
        style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 16, cursor: 'pointer', padding: '6px 18px 6px 8px' }}>
        <i className="ti ti-eye" aria-hidden />
      </button>
    </div>
  );
  return (
    <div data-drop-picker onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 580, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-folder-open" style={{ color: 'var(--accent)', fontSize: 17 }} aria-hidden />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('jdp_title')}</div>
            {docName && <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('jdp_for', { doc: docName })}</div>}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 18, cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding: '10px 20px 9px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder={t('jdp_search')}
              style={{ flex: 1, minWidth: 0, padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
            <button type="button" onClick={() => setBeat(b => b + 1)} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 11.5, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
              <i className="ti ti-refresh" style={{ fontSize: 13 }} aria-hidden />{t('jdp_refresh')}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.45 }}>{hint || t(untilSend ? 'jdp_hint_send' : 'jdp_hint')}</div>
          {examples && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3, lineHeight: 1.45 }}>{t('jdp_hint_example')}</div>}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {showSugg && (
            <>
              <div style={head}>{t('eml_attach_suggested')}</div>
              {sugg === null ? <div style={quiet}>{t('jdp_loading')}</div>
                : !sugg.length ? <div style={quiet}>{t('eml_attach_suggested_none')}</div>
                : sugg.map(f => fileRow(f, `s:${f.id}`))}
              <div style={head}>{t('eml_attach_drop_all')}</div>
            </>
          )}
          {err ? <div style={{ fontSize: 12.5, color: '#B42318', padding: '12px 20px' }}>{err}</div>
            : !data ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '12px 20px' }}>{t('jdp_loading')}</div>
            : !rows.length ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '14px 20px', lineHeight: 1.5 }}>
                {q.trim() ? t('jdp_no_match', { q: q.trim() }) : t('jdp_empty')}
              </div>
            ) : rows.map(f => fileRow(f, f.id))}
        </div>
        {data && <Pagination page={page} total={data.total} limit={data.limit} onPage={setPage} />}
      </div>
    </div>
  );
}
